import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Mail, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { usePageMeta } from '@/hooks/usePageMeta';
import { AudienceCarousel } from '@/components/about/AudienceCarousel';


const team = [
  {
    name: 'Dr. Frank Niles',
    role: 'Business Psychologist & CultureLens Advisor',
    bio: 'Frank helps leaders fix the people issues that hurt performance. He is an IV Talent partner and the author of Communicating with Influence.',
    isPlaceholder: false,
    linkedin: 'https://www.linkedin.com/in/frankniles',
  },
  {
    name: 'Marcelo Couto, MS',
    role: 'CultureLens Advisor',
    bio: 'Marcelo led HR across South America at Ingredion and now advises leaders through IV Talent. He works across the US and Latin America in English, Spanish, and Portuguese.',
    isPlaceholder: false,
    linkedin: 'https://www.linkedin.com/in/coutomarcelo/',
  },
  {
    name: 'Dean McKenna, MSc',
    role: 'CultureLens Advisor',
    bio: 'Founder of IV Talent, fractional CHRO, and board advisor. Works with small and mid-sized businesses and PE-backed companies on leadership, culture, and performance.',
    isPlaceholder: false,
    linkedin: 'https://www.linkedin.com/in/dean-m-6069647/',
  },
];

const aboutJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    url: 'https://culturelens.org/who-we-are',
    name: 'About CultureLens by IV Talent',
    about: {
      '@type': 'Organization',
      name: 'CultureLens',
      url: 'https://culturelens.org/',
      parentOrganization: { '@type': 'Organization', name: 'IV Talent' },
      member: team.map((person) => ({
        '@type': 'Person',
        name: person.name,
        jobTitle: person.role,
        description: person.bio,
        sameAs: [person.linkedin],
      })),
    },
  },
];

export default function WhoWeAre() {
  usePageMeta({
    title: 'About CultureLens by IV Talent',
    description:
      'Meet the team behind CultureLens. A daily read on the people risks that cost small and mid-sized businesses and PE-backed companies money.',
    canonical: 'https://culturelens.org/who-we-are',
    jsonLd: aboutJsonLd,
  });

  const { ref: aboutRef, isVisible: aboutVisible } = useScrollReveal();
  const { ref: teamRef, isVisible: teamVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: audienceRef, isVisible: audienceVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollReveal({ threshold: 0.2 });
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash?.replace('#', '');
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(t);
  }, [location.hash, location.key]);


  return (
    <div className="min-h-screen bg-cream text-ink font-grotesk antialiased">
      <SiteHeader />

      <main className="pt-24 pb-24">
        <div className="container mx-auto px-6 max-w-5xl">
          {/* Back to Home */}
          <Button variant="ghost" asChild className="mb-8">
            <Link to="/" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to home
            </Link>
          </Button>

          {/* Section 1: About IV Talent */}
          <section
            id="about"
            ref={aboutRef}
            className={`mb-24 scroll-mt-24 transition-all duration-1000 ${
              aboutVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-[11px] uppercase tracking-[0.18em] font-grotesk text-brand mb-5">
              Built by IV Talent
            </p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] font-semibold tracking-[-0.025em] leading-[1.05] text-ink max-w-[16ch]">
              CultureLens is built
              <br />
              by IV Talent
            </h1>
            <span aria-hidden className="mt-8 block h-px w-16 bg-brand" />
            <div className="mt-8 max-w-[54ch] space-y-5 text-[17px] text-charcoal/65 leading-[1.7] font-grotesk">
              <p>
                An advisory for small, mid-sized, and PE-backed companies.
              </p>
              <p>
                We pair senior HR experts with technology that reads your organization every
                day, so leaders see what is really happening and act early.
              </p>
            </div>
            <a
              href="https://www.iv-talent.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-flex items-center gap-2 rounded-[4px] border border-warm-neutral/70 px-5 py-2.5 text-sm font-medium text-ink font-grotesk transition-colors hover:border-brand hover:text-brand"
            >
              Visit iv-talent.com
              <ExternalLink className="h-4 w-4" />
            </a>
          </section>

          {/* Section 2: Who CultureLens is for */}
          <section id="who-its-for" ref={audienceRef} className="mb-24 scroll-mt-24">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] text-ink mb-10">Who CultureLens is for</h2>


            <div className={`transition-all duration-700 ${audienceVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <AudienceCarousel />
            </div>
          </section>

          {/* Section 3: Talk to one of us */}
          <section id="contact" ref={teamRef} className="scroll-mt-24">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] text-ink mb-3">Talk to one of us</h2>
            <p className="text-charcoal/70 text-lg mb-12 font-grotesk">
              Reach out to whoever is closest to your situation.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              {team.map((member, index) => (
                <div
                  key={member.name}
                  className={`p-8 rounded-[4px] border border-hairline bg-cream paper-card transition-all duration-700 ${
                    teamVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-start gap-5">
                    {/* Info */}
                    <div className="flex-1">
                      <h3 className="font-display text-xl text-ink mb-1 tracking-[-0.01em]">{member.name}</h3>
                      <p className="text-sm text-brand font-medium mb-3 font-grotesk">{member.role}</p>
                      <p className="text-charcoal/75 leading-relaxed font-grotesk">{member.bio}</p>
                      <div className="flex items-center gap-4 mt-4">
                        {'linkedin' in member && member.linkedin && (
                          <a
                            href={member.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-ink hover:text-brand transition-colors font-grotesk"
                          >
                            <Linkedin className="h-4 w-4" />
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                        {!member.isPlaceholder ? (
                          <a
                            href="mailto:hello@iv-talent.com"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:opacity-80 transition-opacity font-grotesk"
                          >
                            <Mail className="h-3.5 w-3.5" />
                            Contact
                          </a>
                        ) : (
                          <span className="text-sm text-charcoal/60 font-grotesk">Coming soon</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Closing CTA */}
          <section
            ref={ctaRef}
            className={`mt-20 text-center transition-all duration-1000 ${
              ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-charcoal/70 text-lg mb-6 font-grotesk">
              Not sure who to ask for? Email us and we will route you.
            </p>
            <Button asChild className="bg-ink hover:bg-ink-soft text-cream rounded-full px-6 h-11 font-grotesk text-[14px] font-medium">
              <a href="mailto:hello@iv-talent.com">Request access</a>
            </Button>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
